import { ApiError } from "@repo/api-client";
import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useVenue } from "../../shell/VenueContext";
import {
  scheduleApi,
  scheduleKeys,
  type BookingPolicyResponse,
  type GuaranteeMode,
} from "../schedule/scheduleApi";

/**
 * `/v/:venueId/settings/guarantee` — the venue-wide no-show guarantee config (card-fingerprint-no-show.md).
 * The owner turns the guarantee on and sets the fee; enabling it activates the guest card-capture flow (the
 * booking widget then asks for a card) and reveals the "Débits de non-présentation échoués" surface. Writes
 * the shared booking-policy aggregate (CAS `version`), so a concurrent edit surfaces a 409 reload prompt.
 * "Acompte" (deposit) is offered disabled — its capture half is not built yet.
 */
export function GuaranteePolicySettings() {
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const policyQuery = useQuery({
    queryKey: scheduleKeys.policy(venueId),
    queryFn: () => scheduleApi.getPolicy(venueId),
  });

  if (policyQuery.isLoading || !policyQuery.data) {
    return (
      <section className={css({ maxW: "2xl" })}>
        <p className={css({ color: "fg.muted", fontSize: "sm" })}>
          {t("settings.guarantee.loading")}
        </p>
      </section>
    );
  }
  if (policyQuery.isError) {
    return (
      <section role="alert" className={css({ maxW: "2xl", color: "fg.error", fontSize: "sm" })}>
        {t("settings.guarantee.loadError")}
      </section>
    );
  }

  return (
    <GuaranteeForm
      policy={policyQuery.data}
      qcInvalidate={() => qc.invalidateQueries({ queryKey: scheduleKeys.policy(venueId) })}
      venueId={venueId}
    />
  );
}

function GuaranteeForm({
  policy,
  venueId,
  qcInvalidate,
}: {
  policy: BookingPolicyResponse;
  venueId: string;
  qcInvalidate: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const currency = policy.noShowFeeCurrency ?? "EUR";
  const [mode, setMode] = useState<GuaranteeMode>(policy.guaranteeMode);
  // Fee held as a major-unit string for the input; stored as integer minor units (ADR-0024).
  const [feeMajor, setFeeMajor] = useState(
    typeof policy.noShowFeeAmount === "number" ? (policy.noShowFeeAmount / 100).toString() : "",
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [feeError, setFeeError] = useState(false);

  const feeMinor = Math.round((Number.parseFloat(feeMajor.replace(",", ".")) || 0) * 100);

  async function submit() {
    setSaved(false);
    setConflict(false);
    setFeeError(false);
    if (mode === "NO_SHOW_FEE" && feeMinor <= 0) {
      setFeeError(true);
      return;
    }
    setSaving(true);
    try {
      const body: BookingPolicyResponse = {
        ...policy,
        guaranteeMode: mode,
        noShowFeeAmount: mode === "NO_SHOW_FEE" ? feeMinor : null,
        noShowFeeCurrency: mode === "NO_SHOW_FEE" ? currency : null,
      };
      await scheduleApi.savePolicy(venueId, body);
      await qcInvalidate();
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setConflict(true);
      else throw err;
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "5", maxW: "2xl" })}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        <h2 className={css({ fontSize: "lg", fontWeight: "semibold", color: "fg.default" })}>
          {t("settings.guarantee.title")}
        </h2>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("settings.guarantee.intro")}
        </p>
      </div>

      {conflict ? (
        <div
          role="alert"
          className={css({
            p: "3",
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
            bg: "bg.subtle",
            fontSize: "sm",
          })}
        >
          <p>{t("settings.guarantee.conflict")}</p>
          <button
            type="button"
            onClick={() => void qcInvalidate()}
            className={css({ mt: "2", px: "3", py: "1", borderWidth: "1px", rounded: "md" })}
          >
            {t("settings.guarantee.reload")}
          </button>
        </div>
      ) : null}

      <fieldset
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "2",
          border: "none",
          p: "0",
          m: "0",
        })}
      >
        <legend
          className={css({ fontSize: "sm", fontWeight: "medium", color: "fg.default", mb: "1" })}
        >
          {t("settings.guarantee.mode.legend")}
        </legend>
        <ModeRadio
          checked={mode === "OFF"}
          onChange={() => setMode("OFF")}
          label={t("settings.guarantee.mode.off")}
          hint={t("settings.guarantee.mode.offHint")}
        />
        <ModeRadio
          checked={mode === "NO_SHOW_FEE"}
          onChange={() => setMode("NO_SHOW_FEE")}
          label={t("settings.guarantee.mode.noShowFee")}
          hint={t("settings.guarantee.mode.noShowFeeHint")}
        />
        <ModeRadio
          checked={false}
          onChange={() => undefined}
          disabled
          label={t("settings.guarantee.mode.deposit")}
          hint={t("settings.guarantee.mode.depositSoon")}
        />
      </fieldset>

      {mode === "NO_SHOW_FEE" ? (
        <label
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "1",
            fontSize: "sm",
            maxW: "xs",
          })}
        >
          <span className={css({ fontWeight: "medium", color: "fg.default" })}>
            {t("settings.guarantee.fee.label")}
          </span>
          <span className={css({ display: "flex", alignItems: "center", gap: "2" })}>
            <input
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={feeMajor}
              onChange={(e) => setFeeMajor(e.target.value)}
              aria-invalid={feeError}
              className={css({
                flex: "1",
                px: "3",
                py: "2",
                borderWidth: "1px",
                borderColor: feeError ? "border.error" : "border.default",
                rounded: "md",
              })}
            />
            <span className={css({ color: "fg.muted" })}>{currency}</span>
          </span>
          {feeError ? (
            <span role="alert" className={css({ color: "fg.error", fontSize: "xs" })}>
              {t("settings.guarantee.fee.error")}
            </span>
          ) : (
            <span className={css({ color: "fg.muted", fontSize: "xs" })}>
              {t("settings.guarantee.fee.help")}
            </span>
          )}
        </label>
      ) : null}

      <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
        <Button size="sm" loading={saving} onClick={() => void submit()}>
          {t("settings.guarantee.save")}
        </Button>
        {saved ? (
          <output className={css({ fontSize: "sm", color: "fg.muted" })}>
            {t("settings.guarantee.saved")}
          </output>
        ) : null}
      </div>
    </section>
  );
}

function ModeRadio({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  hint: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={css({
        display: "flex",
        gap: "3",
        alignItems: "flex-start",
        p: "3",
        borderWidth: "1px",
        borderColor: checked ? "colorPalette.default" : "border.default",
        rounded: "md",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      })}
    >
      <input
        type="radio"
        name="guaranteeMode"
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className={css({ mt: "0.5" })}
      />
      <span className={css({ display: "flex", flexDirection: "column", gap: "0.5" })}>
        <span className={css({ fontSize: "sm", fontWeight: "medium", color: "fg.default" })}>
          {label}
        </span>
        <span className={css({ fontSize: "xs", color: "fg.muted" })}>{hint}</span>
      </span>
    </label>
  );
}
